import { createReducer, createFeature, on } from '@ngrx/store';

import { Booking } from '../../../objects/booking';
import { LineItem } from '../../../objects/line-item';
import { Order } from '../../../objects/order';
import { OrderActions, OrderTransactionActions, OrderTimeoutActions, ProductActions } from '../actions';
import { Transaction } from '../../../objects/transaction';

export interface State {
  order: Order | null;
  completedOrder: Order | null;
  orderLoading: boolean;
  error: Error | null;

  lastAddedItem: LineItem | Booking | null;
  isItemBeingAdded: boolean;
  isItemBeingRemoved: number[];
  errorProcessingItem: Error | null;

  submittingPromoCode: boolean;
  submittingPromoCodeError: Error | null;

  updatingTip: boolean;
  updatingTipError: Error | null;

  transaction: Transaction | null;
  transactionError: Error | null;
  transactionProcessing: boolean;

  timeout: number;
}

export const initialState: State = {
  order: null,
  completedOrder: null,
  orderLoading: false,
  error: null,

  lastAddedItem: null,
  isItemBeingAdded: false,
  isItemBeingRemoved: [],
  errorProcessingItem: null,

  submittingPromoCode: false,
  submittingPromoCodeError: null,

  updatingTip: false,
  updatingTipError: null,

  transaction: null,
  transactionError: null,
  transactionProcessing: false,

  timeout: 0,
};

export const orderFeature = createFeature({
  name: 'order',
  reducer: createReducer(
    initialState,

    on(OrderActions.clearOrder, state => ({ ...state, order: null, })),
    on(OrderActions.clearUpdatingTipError, state => ({ ...state, updatingTipError: null })),

    on(OrderActions.createOrder, state => ({ ...state, orderLoading: true, error: null, order: null })),
    on(OrderActions.createOrderSuccess, (state, { order }) => ({ ...state, orderLoading: false, error: null, order })),
    on(OrderActions.createOrderFailure, (state, { error }) => ({ ...state, orderLoading: false, error, order: null })),

    on(OrderActions.loadOrder, state => ({ ...state, orderLoading: true, error: null, order: null })),
    on(OrderActions.loadOrderSuccess, (state, { order }) => ({ ...state, orderLoading: false, error: null, order })),
    on(OrderActions.loadOrderFailure, (state, { error }) => ({ ...state, orderLoading: false, error, order: null })),

    on(OrderActions.loadCompletedOrder, state => ({ ...state, orderLoading: true, error: null, completedOrder: null })),
    on(OrderActions.loadCompletedOrderSuccess, (state, { completedOrder }) => ({
      ...state, orderLoading: false, error: null, completedOrder })),
    on(OrderActions.loadCompletedOrderFailure, (state, { error }) => ({ ...state, orderLoading: false, error, completedOrder: null })),

    on(OrderActions.addItem, state => ({ ...state, isItemBeingAdded: true, errorProcessingItem: null, lastAddedItem: null })),
    on(OrderActions.addItemSuccess, (state, { order, lastAddedItem }) => ({
      ...state,
      order,
      isItemBeingAdded: false,
      errorProcessingItem: null,
      lastAddedItem,
    })),
    on(OrderActions.addItemFailure, (state, { error }) => ({
      ...state, isItemBeingAdded: false, errorProcessingItem: error, lastAddedItem: null })),

    on(OrderActions.removeItem, (state, { item }) => ({
      ...state,
      isItemBeingRemoved: [...state.isItemBeingRemoved, item.id],
      errorProcessingItem: null,
    })),
    on(OrderActions.removeItemSuccess, (state, { order, item }) => ({
      ...state,
      isItemBeingRemoved: state.isItemBeingRemoved.filter(id => id !== item.id),
      errorProcessingItem: null,
      order,
    })),
    on(OrderActions.removeItemFailure, (state, { error, item }) => ({
      ...state,
      isItemBeingRemoved: state.isItemBeingRemoved.filter(id => id !== item.id),
      errorProcessingItem: error,
    })),

    on(OrderActions.submitPromoCode, state => ({ ...state, submittingPromoCode: true, submittingPromoCodeError: null })),
    on(OrderActions.submitPromoCodeSuccess, (state, { order }) => ({
      ...state,
      order,
      submittingPromoCode: false,
      submittingPromoCodeError: null,
    })),
    on(OrderActions.submitPromoCodeFailure, (state, { error }) => ({
      ...state,
      submittingPromoCode: false,
      submittingPromoCodeError: error,
    })),

    on(OrderActions.clearSubmittingPromoCode, state => ({
      ...state,
      submittingPromoCode: false,
      submittingPromoCodeError: null,
    })),

    on(OrderActions.removePromoCode, state => ({ ...state, submittingPromoCode: true, submittingPromoCodeError: null })),
    on(OrderActions.removePromoCodeSuccess, (state, { order }) => ({
      ...state,
      order,
      submittingPromoCode: false,
      submittingPromoCodeError: null,
    })),
    on(OrderActions.removePromoCodeFailure, (state, { error }) => ({
      ...state,
      submittingPromoCode: false,
      submittingPromoCodeError: error,
    })),

    on(OrderTransactionActions.beginCheckout, state => ({
      ...state,
      completedOrder: null,
      transaction: null,
      transactionProcessing: false,
      transactionError: null,
    })),
    on(OrderTransactionActions.checkout, state => ({
      ...state,
      completedOrder: null,
      transaction: null,
      transactionProcessing: true,
      transactionError: null,
    })),
    on(OrderTransactionActions.checkoutStatusUpdate, (state, { transaction }) => ({
      ...state, transaction })),
    on(OrderTransactionActions.checkoutSuccess, (state, { transaction, completedOrder }) => ({
      ...state,
      completedOrder,
      transaction,
      transactionProcessing: false,
      transactionError: null,
    })),
    on(OrderTransactionActions.checkoutFailure, (state, { error }) => ({
      ...state,
      completedOrder: null,
      transaction: null,
      transactionProcessing: false,
      transactionError: error,
    })),

    on(OrderActions.submitTip, state => ({
      ...state,
      updatingTip: true,
      updatingTipError: null,
    })),
    on(OrderActions.submitTipSuccess, (state, { order }) => ({
      ...state,
      order,
      updatingTip: false,
      updatingTipError: null,
    })),
    on(OrderActions.submitTipFailure, (state, { error }) => ({
      ...state,
      updatingTip: false,
      updatingTipError: error,
    })),

    /**
     * TODO: Not sure if this is efficient enough, as
     * in the store we might want to override the order that
     * has already been overridden a while ago by one of
     * add/remove/load 'success' actions
     */
    on(OrderTimeoutActions.resetTimeoutSuccess, (state, { order }) => ({
      ...state,
      order,
    })),

    on(OrderTimeoutActions.updateTimeout, (state, { timeout }) => ({
      ...state,
      timeout,
    })),

    /**
     * Have to clear the error when user navigates to product/products
     */
    on(ProductActions.loadProductSuccess, ProductActions.setProduct, (state, _) => ({
      ...state,
      errorProcessingItem: null,
      transactionError: null,
    })),
  ),
});

export const {
  name: orderFeatureKey,
  reducer,
  selectOrderState,
  selectOrder,
  selectCompletedOrder,
  selectOrderLoading,
  selectError: selectOrderError,
  selectLastAddedItem,
  selectIsItemBeingAdded,
  selectIsItemBeingRemoved,
  selectErrorProcessingItem,
  selectSubmittingPromoCode,
  selectSubmittingPromoCodeError,
  selectUpdatingTip,
  selectUpdatingTipError,
  selectTransaction,
  selectTransactionError,
  selectTransactionProcessing,
  selectTimeout,
} = orderFeature;
